import { getFormattedDate } from "../../../../util/time-helpers";
import {
  GetDynamicFormData,
  GetCustomFieldData,
  SaveInitialReviewPeriodDates,
} from "../../../../services/api/task-form-api";
import dayjs from "../../../../util/dayjs-config";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";

export const BadgeConfig = {
  200: {
    color: "warning",
    icon: (
      <FluentUiIcon name={"ArrowSyncRegular"} color={"#242424"} size={20} />
    ),
    text: "PROCESSING",
    tooltip: "Request sent on",
  },
  201: {
    color: "danger",
    icon: (
      <FluentUiIcon name={"ImportantRegular"} color={"#FFFFFF"} size={20} />
    ),
    text: "ERROR",
    tooltip: "Failed to complete the last request on",
  },
  203: {
    color: "informative",
    icon: (
      <FluentUiIcon name={"ErrorCircleRegular"} color={"#616161"} size={20} />
    ),
    text: "NOT REQUESTED",
    tooltip: "",
  },
  202: {
    color: "success",
    icon: (
      <FluentUiIcon
        name={"CheckmarkCircleRegular"}
        color={"#FFFFFF"}
        size={20}
      />
    ),
    text: "COMPLETE",
    tooltip: "Last request successfully completed on",
  },
};

export const FieldTypes = Object.freeze({
  ShortTextEntry: "Short Text Entry",
  LongTextEntry: "Long Text Entry",
  NumericInteger: "Numeric (Integer)",
  NumericDecimal: "Numeric (Decimal)",
  Dropdown: "Dropdown",
  Date: "Date",
  TimeDuration: "Time Duration",
  Checkbox: "Checkbox",
  Toggle: "Toggle",
  Radio: "Radio Buttons",
  Table: "Table",
  TextButtonComboBox: "TextButtonComboBox",
  file: "File",
  URL: "URL",
});

export const BaseFormField = Object.freeze({
  Project: 1,
  TaskName: 2,
  StartDate: 3,
  EndDate: 4,
  TaskType: 5,
  Priority: 6,
});

export const UpdateSubForm = (selectedSubForm, values, isReset = true) => {
  const subForm = JSON.parse(JSON.stringify(selectedSubForm));
  const processedFieldIds = new Set();

  for (let item in values) {
    let fieldToModify = subForm.Fields.find((field) => field.Id === item);

    if (!fieldToModify) continue;

    if (!fieldToModify.IsDisabled && isReset) {
      processedFieldIds.add(item);

      if (fieldToModify.FieldType === FieldTypes.Table) {
        fieldToModify.Table.Rows = [];
      } else if (fieldToModify.FieldType === FieldTypes.file) {
        fieldToModify.Data = "";
        fieldToModify.Files = []; // reset file data
      } else {
        fieldToModify.Data = ""; // reset other data
      }
    } else {
      if (fieldToModify.FieldType === FieldTypes.file) {
        fieldToModify.Files = values[item];
        fieldToModify.Data = "";
      }
      else if (fieldToModify.FieldType !== FieldTypes.Table) {
        // only set values to other data since table data is handled differently
        fieldToModify.Data = values[item];
      }
    }
  }

  if (isReset) {
    subForm.Fields.forEach((field) => {
      if (!field.IsDisabled && !processedFieldIds.has(field.Id)) {
        if (field.FieldType === FieldTypes.file) {
          field.Data = "";
          field.Files = [];
        } else if (field.FieldType === FieldTypes.Table) {
          field.Table.Rows = [];
        }
      }
    });
  }

  return subForm;
};

export const UpdateForm = (subForm, formData) => {
  const form = JSON.parse(JSON.stringify(formData.data));

  let subFormIndex = form.SubForms.findIndex((sf) => sf.Id === subForm.Id);
  form.SubForms[subFormIndex] = subForm;

  return { form, subFormIndex };
};

export const CompareDataWithApiName = (
  formData,
  field,
  value,
  selectedSubFormId
) => {
  const form = JSON.parse(JSON.stringify(formData.data));
  let currentSubForm = null;

  for (let subForm of form.SubForms) {
    for (let f of subForm.Fields) {
      if (f.APIName && f.APIName === field.APIName) {
        if (f.FieldType === FieldTypes.file) {
          f.Files = value;
        } else if (f.FieldType === FieldTypes.Table) {
          f.Table = value;
        } else {
          f.Data = value;
        }
      }
    }
    if (selectedSubFormId === subForm.Id) currentSubForm = subForm;
  }

  return { currentSubForm, form };
};

export const IsChangeEvent = (field) => {
  let result;
  switch (field.FieldType) {
    case FieldTypes.Dropdown:
    case FieldTypes.Date:
    case FieldTypes.Toggle:
      result = "true";
      break;
    case FieldTypes.Checkbox:
      result = "multiSelect";
      break;
    case FieldTypes.ShortTextEntry:
    case FieldTypes.LongTextEntry:
    case FieldTypes.NumericDecimal:
    case FieldTypes.NumericInteger:
    default:
      result = "false";
      break;
  }

  return result;
};

export const GetValue = (field, e, value) => {
  let result;
  switch (field.FieldType) {
    case FieldTypes.Dropdown:
      result = value.optionValue;
      break;
    case FieldTypes.Toggle:
      result = e.target.checked ? "selected" : "not selected";
      break;
    case FieldTypes.Date:
      result = e ? getFormattedDate(new Date(e)) : "";
      break;
    default:
      break;
  }

  return result;
};

export const InitializeForm = (subForm) => {
  if (subForm) {
    let initialValues = {};
    subForm?.Fields?.forEach((field) => {
      initialValues[field.Id] = field.Data;
    });
    return initialValues;
  }
};

export const GetCurrentSubFormIndex = (form, selectedSubForm) => {
  if (!selectedSubForm) return 0;
  return form.SubForms.findIndex((sf) => sf.Id === selectedSubForm.Id);
};

export const processCustomFormula = async (
  fieldId,
  customFormula,
  formData
) => {
  try {
    const form = JSON.parse(JSON.stringify(formData.data));
    const result = await GetCustomFieldData(fieldId, customFormula, form);
    if (result.status === 200) {
      return result.data;
    }
    return "";
  } catch (error) {
    console.error("Error processing custom formula:", error);
  }
};

export const preprocessConditions = (fields) => {
  const conditionMap = {};
  fields.forEach((field) => {
    field.Conditions?.forEach((condition) => {
      let key, value;
      if (condition.Condition.includes("=")) {
        [key, value] = condition.Condition.split("=");
      } else {
        key = field.Id;
        value = condition.Condition;
      }

      if (!conditionMap[value]) {
        conditionMap[value] = [];
      }
      conditionMap[value].push({
        fieldId: field.Id,
        key,
        value: condition.Value,
        customFormula: condition.CustomFormula,
      });
    });
  });

  return conditionMap;
};

export const LoadFormData = async (params, props) => {
  let result;
  let conditionMap = {};
  let formStatusMap = {};
  if (props.IsView) {
    result = { data: props.FormData };
  } else {
    result = await GetDynamicFormData({
      Id: params.formId,
      taskId: parseInt(params.taskId),
    });
    conditionMap = preprocessConditions(
      result.data?.SubForms.flatMap((sf) => sf.Fields) || []
    );
    formStatusMap = {
      StatusColor: result.data.StatusColor,
      StatusColorBackground: result.data.StatusColorBackground,
      StatusColorBorder: result.data.StatusColorBorder,
      StatusColorFont: result.data.StatusColorFont,
      StatusName: result.data.StatusName,
    };
  }
  return { result, conditionMap, formStatusMap };
};

export const processConditionsAndUpdateFields = async (
  currentSubForm,
  conditions,
  value,
  formData
) => {
  await Promise.all(
    currentSubForm.Fields.map(async (f) => {
      const matchedCondition = conditions.find((cond) => f.Id === cond.fieldId);

      if (matchedCondition) {
        if (value === matchedCondition.value) {
          f.IsVisible = true;
          f.Data = matchedCondition.customFormula
            ? await processCustomFormula(
              f.Id,
              matchedCondition.customFormula,
              formData
            )
            : f.Data;
        } else {
          f.IsVisible = false;
          f.Data = null;
        }
      }
      return f;
    })
  );

  return currentSubForm;
};

export const processInitialConditions = async (formData) => {
  const updatedSubForms = await Promise.all(
    formData.SubForms?.map(async (subForm) => {
      if (Array.isArray(subForm.Fields)) {
        subForm.Fields = await Promise.all(
          subForm.Fields?.map(async (f) => {
            if (f.Conditions) {
              for (const condition of f.Conditions) {
                if (condition.Condition.includes("=")) {
                  const [key, value] = condition.Condition.split("=");
                  if (key === "DynamicFieldId") {
                    const targetField = subForm.Fields.find(
                      (field) => field.DynamicFieldId.toString() === value
                    );
                    if (targetField && targetField.Data === condition.Value) {
                      f.IsVisible = true;
                    }
                  }
                }
              }
            }
            return f;
          })
        );
      }
      return subForm;
    })
  );

  return { ...formData, SubForms: updatedSubForms };
};

export const handleCustomFormulaClickUtil = async (
  field,
  formData,
  SetDataWithSameApiName
) => {
  const updatedData = await processCustomFormula(
    field.Id,
    field.Conditions.find((cond) => cond.CustomFormula)?.CustomFormula,
    formData
  );
  SetDataWithSameApiName(field, updatedData);
};

export const checkAndSaveInitialReviewPeriodDates = async (
  formData,
  params
) => {
  const firstSubForm = formData.data.SubForms[0];
  const hasRelevantConditions =
    firstSubForm.Fields.length >= 2 &&
    (firstSubForm.Fields[0].Conditions.some(
      (c) =>
        c.Condition.includes("2yearsFromCurrentDate") ||
        c.Condition.includes("CurrentDate")
    ) ||
      firstSubForm.Fields[1].Conditions.some(
        (c) =>
          c.Condition.includes("2yearsFromCurrentDate") ||
          c.Condition.includes("CurrentDate")
      ));

  if (hasRelevantConditions) {
    const values = {};
    values[firstSubForm.Fields[0].Id] = firstSubForm.Fields[0].Data;
    values[firstSubForm.Fields[1].Id] = firstSubForm.Fields[1].Data;

    const { form } = UpdateForm(firstSubForm, formData);

    await SaveInitialReviewPeriodDates({
      ...form,
      TaskId: params.taskId,
      IsSubmit: false,
    });
  }
};

export const processFieldConditions = (
  field,
  formFields,
  fieldComponentProps,
  SetDataWithSameApiName,
  IsView
) => {
  field.Conditions?.forEach((condition) => {
    const [key, value] = getConditionKeyValue(condition, field);

    if (shouldDisableButton(key, value, field.Id, IsView)) {
      fieldComponentProps.buttonDisabled = condition.Value === "true";
    }

    if (value === "2yearsFromCurrentDate") {
      handle2YearsFromCurrentDateCondition(
        key,
        value,
        field,
        formFields,
        fieldComponentProps,
        SetDataWithSameApiName
      );
    }

    if (value === "CurrentDate") {
      handleCurrentDateCondition(
        key,
        value,
        field,
        formFields,
        fieldComponentProps,
        SetDataWithSameApiName
      );
    }
  });
};

const getConditionKeyValue = (condition, field) => {
  return condition.Condition.includes("=")
    ? condition.Condition.split("=")
    : [field.Id, condition.Condition];
};

const shouldDisableButton = (key, value, fieldId, IsView) => {
  return key === fieldId && value === "RefreshButton" && !IsView;
};

const handle2YearsFromCurrentDateCondition = (
  key,
  value,
  field,
  formFields,
  fieldComponentProps,
  SetDataWithSameApiName
) => {
  if (key !== field.Id) return;

  const relatedField = findRelatedField(formFields, "CurrentDate");
  const relatedFieldDate = relatedField?.Data
    ? dayjs(relatedField.Data)
    : dayjs();
  const maxDate = relatedFieldDate.endOf("day");
  const today = dayjs();
  const minDate = today.subtract(2, "years").startOf("day");

  fieldComponentProps.minDate = minDate.toDate();
  fieldComponentProps.maxDate = maxDate.toDate();

  if (
    relatedField &&
    (!relatedField.Data || relatedFieldDate.isAfter(maxDate))
  ) {
    SetDataWithSameApiName(relatedField, maxDate.toDate());
  }
};

const handleCurrentDateCondition = (
  key,
  value,
  field,
  formFields,
  fieldComponentProps,
  SetDataWithSameApiName
) => {
  if (key !== field.Id) return;

  const relatedField = findRelatedField(formFields, "2yearsFromCurrentDate");
  const relatedFieldDate = relatedField?.Data
    ? dayjs(relatedField.Data)
    : dayjs();
  const minDate = relatedFieldDate.startOf("day");

  fieldComponentProps.minDate = minDate.toDate();
  fieldComponentProps.maxDate = dayjs().endOf("day").toDate();

  if (
    relatedField &&
    (!relatedField.Data || relatedFieldDate.isBefore(minDate))
  ) {
    SetDataWithSameApiName(relatedField, minDate.toDate());
  }
};

const findRelatedField = (formFields, conditionValue) => {
  return formFields.find((f) =>
    f.Conditions.some((c) => c.Condition === conditionValue)
  );
};

// ── Dynamic Form Automations ──────────────────────────────────────────────

/**
 * Automation When EventTypes (triggers)
 */
export const AutomationWhenTypes = Object.freeze({
  TaskCreated: "Task Created",
  FormStatusChangedTo: "Form Status Changed To",
  FieldInput: "Field Input",
  FormSubmission: "Form Submission",
});

/**
 * Automation Then EventTypes (actions)
 */
export const AutomationThenTypes = Object.freeze({
  ChangeFormStatusTo: "Change Form Status To",
  FormAvailability: "Form Availability",
  SectionAvailability: "Section Availability",
  FieldAvailability: "Field Availability",
});

/**
 * Preprocesses automation rules into efficient lookup maps grouped by trigger type.
 *
 * @param {Array} automations - Raw automation array from the API
 * @returns {{ fieldInputMap: Object, formStatusMap: Object, taskCreatedList: Array, formSubmissionList: Array }}
 */
export const preprocessAutomations = (automations) => {
  const fieldInputMap = {};
  const formStatusAutomationMap = {};
  const taskCreatedList = [];
  const formSubmissionList = [];

  if (!automations || automations.length === 0) {
    return { fieldInputMap, formStatusAutomationMap, taskCreatedList, formSubmissionList };
  }

  const sorted = [...automations].sort(
    (a, b) =>
      a.DynamicFormAutomation.AutomationOrder -
      b.DynamicFormAutomation.AutomationOrder
  );

  sorted.forEach((automation) => {
    const entry = {
      thenActions: automation.DynamicFormAutomationThen,
      order: automation.DynamicFormAutomation.AutomationOrder,
    };

    automation.DynamicFormAutomationWhen.forEach((when) => {
      switch (when.EventType) {
        case AutomationWhenTypes.FieldInput: {
          if (when.EventDynamicFieldId != null) {
            const key = when.EventDynamicFieldId.toString();
            if (!fieldInputMap[key]) fieldInputMap[key] = [];
            fieldInputMap[key].push({ ...entry, when });
          }
          break;
        }
        default:
          break;
      }
    });
  });

  return { fieldInputMap, formStatusAutomationMap, taskCreatedList, formSubmissionList };
};

/**
 * Applies a list of "then" actions to the form.
 * Handles: Section Availability, Field Availability, Change Form Status To, Form Availability.
 *
 * Mutates the `form` object in place.
 *
 * @param {Object} form        - The full form data object (form.SubForms)
 * @param {Array}  thenActions  - Array of DynamicFormAutomationThen entries
 * @returns {{ statusChange: { statusId: string, statusName: string } | null, formAvailability: string | null }}
 */
const applyThenActions = (form, thenActions) => {
  let statusChange = null;
  let formAvailability = null;

  thenActions.forEach((action) => {
    const then = action.DynamicFormAutomationThen;

    switch (then.EventType) {
      case AutomationThenTypes.SectionAvailability: {
        if (then.EventDynamicSectionId != null) {
          const targetSectionId = then.EventDynamicSectionId;
          form.SubForms.forEach((sf) => {
            if (sf.Id === targetSectionId) {
              sf.Fields.forEach((f) => {
                f.IsAutomationControlled = true;
                f.IsVisible = then.EventDynamicFieldVisible;
                f.IsDisabled = !then.EventDynamicFieldEditable;
                f.IsRequired = f._baseIsRequired ? true : then.EventDynamicFieldMandatory;
              });
            }
          });
        }
        break;
      }
      case AutomationThenTypes.FieldAvailability: {
        if (then.EventDynamicFieldId != null) {
          const targetFieldId = then.EventDynamicFieldId;
          form.SubForms.forEach((sf) => {
            sf.Fields.forEach((f) => {
              if (f.DynamicFieldId === targetFieldId) {
                f.IsAutomationControlled = true;
                f.IsVisible = then.EventDynamicFieldVisible;
                f.IsDisabled = !then.EventDynamicFieldEditable;
                // Never clear IsRequired if the field was originally required on load
                f.IsRequired = f._baseIsRequired ? true : then.EventDynamicFieldMandatory;
              }
            });
          });
        }
        break;
      }
      default:
        break;
    }
  });

  return { statusChange, formAvailability };
};

/**
 * Collects all section and field IDs affected by a set of automations,
 * then resets them to a default hidden/disabled state before re-applying.
 */
const resetAffectedTargets = (form, automations) => {
  const affectedFieldIds = new Set();
  const affectedSectionIds = new Set();

  automations.forEach((auto) => {
    auto.thenActions.forEach((action) => {
      const then = action.DynamicFormAutomationThen;
      if (then.EventType === AutomationThenTypes.FieldAvailability && then.EventDynamicFieldId != null) {
        affectedFieldIds.add(then.EventDynamicFieldId);
      }
      if (then.EventType === AutomationThenTypes.SectionAvailability && then.EventDynamicSectionId != null) {
        affectedSectionIds.add(then.EventDynamicSectionId);
      }
    });
  });

  form.SubForms.forEach((sf) => {
    // Reset child fields of affected sections
    if (affectedSectionIds.has(sf.Id)) {
      sf.Fields.forEach((f) => {
        f.IsAutomationControlled = true;
        f.IsVisible = false;
        f.IsDisabled = true;
        f.IsRequired = f._baseIsRequired ? true : false;
      });
    }

    // Reset individually targeted fields
    sf.Fields.forEach((f) => {
      if (affectedFieldIds.has(f.DynamicFieldId)) {
        f.IsAutomationControlled = true;
        f.IsVisible = false;
        f.IsDisabled = true;
        f.IsRequired = f._baseIsRequired ? true : false;
      }
    });
  });
};

/**
 * Applies "Field Input" automations when a specific field's value changes.
 *
 * 1. Resets all sections/fields affected by automations for this trigger field.
 * 2. Re-applies only the automations whose trigger value matches.
 *
 * Mutates the `form` object in place (caller should pass a deep clone).
 *
 * @param {Object} form        - The full form data object (form.SubForms)
 * @param {Object} fieldInputMap - Preprocessed automation lookup map
 * @param {string|number} changedFieldDynamicId - The DynamicFieldId that changed
 * @param {string} newValue    - The new value of the changed field
 * @returns {{ statusChange: object|null, formAvailability: string|null }}
 */
export const applyFieldInputAutomations = (
  form,
  fieldInputMap,
  changedFieldDynamicId,
  newValue
) => {
  const key = changedFieldDynamicId.toString();
  const automations = fieldInputMap[key];
  if (!automations || automations.length === 0) return { statusChange: null, formAvailability: null };

  // Reset affected targets before re-applying
  resetAffectedTargets(form, automations);

  // Apply matching automations (trigger value matches the new field value)
  let result = { statusChange: null, formAvailability: null };
  automations.forEach((auto) => {
    const isMatch =
      auto.when.EventTypeTrigger === "Value"
        ? newValue === auto.when.EventTypeValue
        : false;

    if (isMatch) {
      const actionResult = applyThenActions(form, auto.thenActions);
      if (actionResult.statusChange) result.statusChange = actionResult.statusChange;
      if (actionResult.formAvailability) result.formAvailability = actionResult.formAvailability;
    }
  });

  return result;
};

/**
 * Re-evaluates ALL field input automations using current field values.
 *
 * Unlike applyFieldInputAutomations (which only processes one trigger field),
 * this performs a unified reset-then-reapply across every trigger field.
 * This prevents overlapping automation targets from being incorrectly hidden
 * when multiple trigger fields share the same target sections/fields.
 *
 * Mutates the `form` object in place (caller should pass a deep clone).
 *
 * @param {Object} form          - The full form data object (form.SubForms)
 * @param {Object} fieldInputMap - Preprocessed automation lookup keyed by DynamicFieldId
 * @returns {{ statusChange: object|null, formAvailability: string|null }}
 */
export const reapplyAllFieldInputAutomations = (form, fieldInputMap) => {
  if (!fieldInputMap || Object.keys(fieldInputMap).length === 0) {
    return { statusChange: null, formAvailability: null };
  }

  const allFields = form.SubForms.flatMap((sf) => sf.Fields);
  const localFieldIds = new Set(allFields.map((f) => f.DynamicFieldId?.toString()));

  // Only reset/reapply automations whose trigger field exists in this form.
  // Cross-form automations (trigger field belongs to another form) are already
  // evaluated by the backend; resetting their targets here would undo backend state.
  const localAutomations = [];
  Object.entries(fieldInputMap).forEach(([dynamicFieldId]) => {
    if (localFieldIds.has(dynamicFieldId)) {
      localAutomations.push(...fieldInputMap[dynamicFieldId]);
    }
  });

  resetAffectedTargets(form, localAutomations);

  let result = { statusChange: null, formAvailability: null };
  const sectionActions = [];
  const otherActions = [];

  Object.entries(fieldInputMap).forEach(([dynamicFieldId, automations]) => {
    const field = allFields.find(
      (f) => f.DynamicFieldId?.toString() === dynamicFieldId
    );
    if (!field) return;

    automations.forEach((auto) => {
      const isMatch =
        auto.when.EventTypeTrigger === "Value"
          ? field.Data === auto.when.EventTypeValue
          : false;

      if (isMatch) {
        auto.thenActions.forEach((action) => {
          const eventType = action.DynamicFormAutomationThen?.EventType;
          if (eventType === AutomationThenTypes.SectionAvailability) {
            sectionActions.push(action);
          } else {
            otherActions.push(action);
          }
        });
      }
    });
  });

  const applyBatch = (actions) => {
    if (actions.length === 0) return;
    const actionResult = applyThenActions(form, actions);
    if (actionResult.statusChange) result.statusChange = actionResult.statusChange;
    if (actionResult.formAvailability) result.formAvailability = actionResult.formAvailability;
  };

  applyBatch(sectionActions);
  applyBatch(otherActions);

  return result;
};

/**
 * Applies automations triggered by the current form status.
 * Called on initial load and whenever the form status changes.
 *
 * @param {Object} form                   - The full form data object
 * @param {Object} formStatusAutomationMap - Preprocessed map keyed by status ID
 * @param {string} currentStatusId         - The current form status ID
 * @returns {{ statusChange: object|null, formAvailability: string|null }}
 */
export const applyFormStatusAutomations = (
  form,
  formStatusAutomationMap,
  currentStatusId
) => {
  if (!formStatusAutomationMap || !currentStatusId) {
    return { statusChange: null, formAvailability: null };
  }

  const automations = formStatusAutomationMap[currentStatusId];
  if (!automations || automations.length === 0) {
    return { statusChange: null, formAvailability: null };
  }

  let result = { statusChange: null, formAvailability: null };
  automations.forEach((auto) => {
    const actionResult = applyThenActions(form, auto.thenActions);
    if (actionResult.statusChange) result.statusChange = actionResult.statusChange;
    if (actionResult.formAvailability) result.formAvailability = actionResult.formAvailability;
  });

  return result;
};

/**
 * Applies automations triggered by "Task Created".
 * Always applied on initial form load since the task already exists.
 *
 * @param {Object} form            - The full form data object
 * @param {Array}  taskCreatedList - Preprocessed list of task-created automations
 * @returns {{ statusChange: object|null, formAvailability: string|null }}
 */
export const applyTaskCreatedAutomations = (form, taskCreatedList) => {
  if (!taskCreatedList || taskCreatedList.length === 0) {
    return { statusChange: null, formAvailability: null };
  }

  let result = { statusChange: null, formAvailability: null };
  taskCreatedList.forEach((auto) => {
    const actionResult = applyThenActions(form, auto.thenActions);
    if (actionResult.statusChange) result.statusChange = actionResult.statusChange;
    if (actionResult.formAvailability) result.formAvailability = actionResult.formAvailability;
  });

  return result;
};

/**
 * Applies automations triggered by "Form Submission".
 * Called when the user submits/completes the form.
 *
 * @param {Object} form               - The full form data object
 * @param {Array}  formSubmissionList  - Preprocessed list of form-submission automations
 * @returns {{ statusChange: object|null, formAvailability: string|null }}
 */
export const applyFormSubmissionAutomations = (form, formSubmissionList) => {
  if (!formSubmissionList || formSubmissionList.length === 0) {
    return { statusChange: null, formAvailability: null };
  }

  let result = { statusChange: null, formAvailability: null };
  formSubmissionList.forEach((auto) => {
    const actionResult = applyThenActions(form, auto.thenActions);
    if (actionResult.statusChange) result.statusChange = actionResult.statusChange;
    if (actionResult.formAvailability) result.formAvailability = actionResult.formAvailability;
  });

  return result;
};

/**
 * Evaluates all applicable automations on initial form load:
 * 1. "Task Created" automations (always apply)
 * 2. "Form Status Changed To" automations (based on current status)
 * 3. "Field Input" automations (based on current field values)
 *
 * @param {Object} form          - The full form data object
 * @param {Object} automationMap - Full preprocessed automation map
 * @param {string} currentFormStatusId - The current form status ID
 * @returns {Object} The mutated form object
 */
export const applyInitialAutomations = (form, automationMap, currentFormStatusId) => {
  // Stamp _baseIsRequired on every field before any automations run,
  // so we capture the true API-loaded IsRequired value.
  form.SubForms.forEach((sf) => {
    sf.Fields.forEach((f) => {
      if (f._baseIsRequired === undefined) {
        f._baseIsRequired = !!f.IsRequired;
      }
    });
  });

  // 1. Task Created automations
  applyTaskCreatedAutomations(form, automationMap.taskCreatedList);

  // 2. Form Status automations
  if (currentFormStatusId) {
    applyFormStatusAutomations(form, automationMap.formStatusAutomationMap, currentFormStatusId);
  }

  // 3. Field Input automations — unified reset-then-reapply to avoid
  //    overlapping targets being incorrectly hidden by per-field resets.
  if (automationMap.fieldInputMap && Object.keys(automationMap.fieldInputMap).length > 0) {
    reapplyAllFieldInputAutomations(form, automationMap.fieldInputMap);
  }

  return form;
};

export const isTextLikeField = (ft) =>
  ft === FieldTypes.ShortTextEntry ||
  ft === FieldTypes.LongTextEntry ||
  ft === FieldTypes.NumericDecimal ||
  ft === FieldTypes.NumericInteger ||
  ft === FieldTypes.URL;